// @ts-strict-ignore
import pick from 'lodash/pick';
import queryString from 'query-string';

import {getLocation} from 'common/util/url';

import {getProjectId, getWorkspaceId} from './report/util';
import {isStandalone} from './report/util/env';
import {
  FeatureGate,
  FeatureGatesValue,
  ProjectFeatureFlag,
  RestrictedReport,
  RestrictEntitySaving,
  UserFeatureFlag,
} from './report/constants';
import {installDevFetchCache} from './report/queries/utils/fetch-cache';
import {currentProjectStore, ProjectDetails} from './report/stores/project-details';
import {userDetailsStore} from './report/stores/user-details';
import {userCookieUtils} from './report/util/user-identity-from-cookie';

export class MPContext {
  declare _projectId: number;
  declare isStandalone: boolean;
  declare urlOptions: {[key: string]: string};
  declare currentProject: ProjectDetails;
  declare userEmail: string;
  declare userId: number;
  declare userName: string;
  declare workspaceId: number;

  constructor({projectId = null} = {}) {
    this.projectId = parseInt(projectId || getProjectId(), 10);
    this.isStandalone = isStandalone();
    this.workspaceId = parseInt(getWorkspaceId(), 10);

    // TODO transfer user data to Panel Context combined w/ user details store
    this.userEmail = ``;
    this.userName = ``;
    this.userId = 0;
    const user = userCookieUtils.getUserIdentityFromCookie();
    if (user) {
      this.userEmail = user.email;
      this.userName = user.name;
      this.userId = user.id;
    } else {
      // fall back to network data if cookie is absent (should only happen
      // regularly in localhost dev)
      userDetailsStore
        .subscribe(() => {
          const userVal = userDetailsStore.value;
          if (userVal) {
            this.userEmail = userVal.email;
            this.userName = userVal.name;
            this.userId = userVal.id;
          }
        })
        .fetch();
    }

    if (this.isStandalone) {
      this.urlOptions = queryString.parse(getLocation().search);
      if (`fetch_cache` in this.urlOptions) {
        // hydrate cache (default) - respond from cache if match, fetch in background and update cache for next time
        // simple cache - respond from cache if match, otherwise fetch from network and respond
        const hydrate = this.urlOptions.fetch_cache !== `simple`;
        installDevFetchCache(hydrate);
      }
    }
  }

  get projectId() {
    return this._projectId;
  }

  set projectId(projectId) {
    if (projectId !== this._projectId) {
      this._projectId = projectId;
      if (this.projectId) {
        currentProjectStore
          .subscribe(() => (this.currentProject = currentProjectStore.value))
          .fetch({projectId: this.projectId});
      }
    }
  }

  /**
   * @deprecated
   * Call currentProjectStore.value?.hasFeatureFlag() directly
   */
  hasProjectFeatureFlag(name: ProjectFeatureFlag): boolean {
    return currentProjectStore.value?.hasFeatureFlag(name);
  }

  /**
   * @deprecated
   * Call userDetailsStore.value?.featureFlags.has() directly
   */
  hasUserFeatureFlag(name: UserFeatureFlag): boolean {
    return userDetailsStore.value?.featureFlags.has(name);
  }

  /**
   * @deprecated
   * Call currentProjectStore.value?.getFeatureGate() directly
   */
  hasFeatureGate(name: FeatureGate): boolean {
    return !!currentProjectStore.value?.getFeatureGate(name);
  }

  /**
   * @deprecated
   * Call currentProjectStore.value?.getFeatureGate() directly
   */
  getFeatureGateValue<K extends keyof FeatureGatesValue>(name: K): FeatureGatesValue[K] | null {
    return currentProjectStore.value?.getFeatureGate(name);
  }

  /**
   * @deprecated
   * Call currentProjectStore.value?.canSaveEntity() directly
   */
  canSaveEntity(name: RestrictEntitySaving): boolean {
    return currentProjectStore.value?.canSaveEntity(name);
  }

  canUseCohorts(): boolean {
    return (
      currentProjectStore.value?.hasAccessToReport(RestrictedReport.Cohorts) ||
      this.hasProjectFeatureFlag(ProjectFeatureFlag.Cohorts)
    );
  }

  canUseDataGroups(): boolean {
    return this.hasProjectFeatureFlag(ProjectFeatureFlag.B2BAnalytics) || this.hasFeatureGate(FeatureGate.B2BAnalytics);
  }

  public getUserTrackingData() {
    return pick(this, [`projectId`, `userEmail`, `userId`, `userName`]);
  }
}

// singleton mpContext so we don't have to $child thread mpContext through the component chain
// mpContext as a pattern will be cleaned up later but this is the lesser evil
let mpContext: MPContext = null;

/**
 * @deprecated
 */
export function getMpContext(): MPContext {
  if (!mpContext) {
    mpContext = new MPContext();
  }
  return mpContext;
}
